import { useMemo } from 'react';

import type {
  ConnectionDeleteResult,
  ConnectionListResult,
  ConnectionRepository,
  ConnectionVerifyResult,
  NormalizedConnection,
  PlatformUrn,
} from '@mint-lib/integration-registry';
import { useQuery } from '@mint-lib/query-client';
import type { QueryClient } from '@mint-lib/query-client';
import { PlatformUrns } from '@mint-sdk/api-integrations';
import { Integrations } from '@mint-sdk/api-omni-trafficking';

/** CM is a fixed `platform_v2` URN — this repository only ever serves that one integration. */
export const CM_URN: PlatformUrn =
  PlatformUrns.TRAFFICKING_PLATFORM_URN.campaignManager;

/**
 * Ported from the "AdServer" branch of
 * `packages/connections/src/api/queries/useGetConnectionList.ts`,
 * `useVerifyConnection.ts` and
 * `packages/connections/src/features/ManageConnection/useDeleteConnection.tsx`.
 * CM is always an `AdserverUrnEnum` member, so this repository never needs to
 * branch on `params.platform` the way the legacy shared hooks did.
 */
export interface CMNormalizedConnectionExtra extends Record<string, unknown> {
  campaignManagerAdvertisers:
    | { id: string; name: string; floodlightConfigId: string }[]
    | null;
  campaignManagerProfile: { id: string; name: string } | null;
  campaignManagerIsPaidSearch: boolean | null;
  campaignManagerIsFloodlightEligible: boolean | null;
  campaignManagerIsDv360Linked: boolean | null;
}

/** Mirrors the legacy `AxiosError` shape read in `useVerifyConnection`/`useGetConnectionList`. */
interface CMApiError {
  response?: { data?: { error?: { type?: string; message?: string } } };
}

/** Maps an AdServer list item (CM's branch of `OmniTraffickingIntegration`) into the shared shape. */
export function toNormalizedCMConnection(item: {
  id: string;
  name: string;
  expired?: boolean | null;
  usingInAccounts?: boolean | null;
  userId?: string | null;
  extra: CMNormalizedConnectionExtra;
}): NormalizedConnection {
  return {
    id: item.id,
    name: item.name,
    platformUrn: CM_URN,
    // The list endpoint doesn't return verification state — `useVerify` fetches it separately.
    isVerified: null,
    isExpired: Boolean(item.expired),
    usingInAccounts: Boolean(item.usingInAccounts),
    userId: item.userId ?? null,
    extra: {
      campaignManagerAdvertisers: item.extra.campaignManagerAdvertisers,
      campaignManagerProfile: item.extra.campaignManagerProfile,
      campaignManagerIsPaidSearch: item.extra.campaignManagerIsPaidSearch,
      campaignManagerIsFloodlightEligible:
        item.extra.campaignManagerIsFloodlightEligible,
      campaignManagerIsDv360Linked: item.extra.campaignManagerIsDv360Linked,
    } satisfies CMNormalizedConnectionExtra,
  };
}

function useList(_params: { platform: string }): ConnectionListResult {
  const query = useQuery({
    ...Integrations.getIntegrationList({
      params: {
        adServer: PlatformUrns.TRAFFICKING_PLATFORM_URN.campaignManager,
      },
    }),
  });

  const connections = useMemo<NormalizedConnection[]>(
    () => (query.data ?? []).map(toNormalizedCMConnection),
    [query.data],
  );

  return { connections, isLoading: query.isLoading, refetch: query.refetch };
}

function useVerify(params: {
  platform: string;
  connectionId: string;
}): ConnectionVerifyResult {
  const query = useQuery({
    ...Integrations.getIntegrationIsVerified({
      params: {},
      urlParams: { integrationId: params.connectionId },
    }),
    enabled: Boolean(params.connectionId),
  });

  const apiError = query.error as CMApiError | null;

  return {
    isVerified: query.data,
    isLoading: query.isLoading,
    isWrongCustomerError:
      apiError?.response?.data?.error?.type === 'WrongCustomerError',
    refetch: query.refetch,
  };
}

function useDelete(): ConnectionDeleteResult {
  const { mutate, isPending } = Integrations.deleteIntegration();

  return {
    remove: (id, { onSuccess, onError }) => {
      mutate({ urlParams: { integrationId: id } }, { onSuccess, onError });
    },
    isPending,
  };
}

/** `ConnectionRepository` implementation for CM — the AdServer branch of the legacy dispatch hooks. */
export const cmConnectionRepository: ConnectionRepository = {
  useList,
  useVerify,
  useDelete,
};

/**
 * Loader-safe (non-hook) equivalent of `useList`, for `definition.ts`'s
 * `prefetch`. Router loaders run outside React, so they can't call the
 * `useList` hook — they fetch the same query directly through the
 * `QueryClient` instead.
 */
export async function fetchCMConnections(
  queryClient: QueryClient,
): Promise<NormalizedConnection[]> {
  const items = await queryClient.ensureQueryData(
    Integrations.getIntegrationList({
      params: {
        adServer: PlatformUrns.TRAFFICKING_PLATFORM_URN.campaignManager,
      },
    }),
  );

  return (items ?? []).map(toNormalizedCMConnection);
}
